import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { serviceurl } from 'src/app/constant/constant';
import { Listing, ListingCategory, LicenseType } from './marketplace.models';
import { unwrapPage, isPaginated, pageCount, Paginated } from './pagination.util';

/** Store query params, named as the API expects; arrays are multi-value filters. */
export type StoreQuery = Record<string, string | string[] | undefined>;

export interface FacetOption {
  value: string;
  count: number;
}

/** GET /listings/facets/: option lists per filter param, plus price ceiling and total. */
export interface StoreFacets {
  [param: string]: FacetOption[] | number | string[] | string;
  price_max: string | number; // Decimal arrives as a string
  total: number;
}

export interface ListingPage {
  items: Listing[];
  count: number;
  hasNext: boolean;
  page: number;
}

export interface ListingQuery {
  vendor?: string;
  listing_type?: string;
  category?: string;
  search?: string;
  /** '1' includes the seller's own drafts / delisted items (vendor dashboard only). */
  include_unpublished?: string;
}

@Injectable({ providedIn: 'root' })
export class ListingService {
  private apiUrl = serviceurl + 'api/v1';

  constructor(private http: HttpClient) {}

  list(query: ListingQuery = {}): Observable<Listing[]> {
    let params = new HttpParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value) {
        params = params.set(key, value);
      }
    });
    return this.http.get<Paginated<Listing> | Listing[]>(`${this.apiUrl}/listings/`, { params }).pipe(unwrapPage<Listing>());
  }

  /**
   * Page-aware variant of `list()`. The API paginates at 10 per page, so the
   * plain `list()` silently returns only the first page — fine for a rail,
   * not for the full store grid.
   */
  /**
   * Store search: one page of `GET /listings/` with the store's filters. A
   * value that is an array is sent as repeated params (?genre=A&genre=B) —
   * the API matches ANY of them, and several values contain commas, so they
   * can't be joined.
   */
  search(query: StoreQuery, page = 1): Observable<ListingPage> {
    let params = new HttpParams();
    Object.entries(query).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.filter(Boolean).forEach((v) => (params = params.append(key, v)));
      } else if (value) {
        params = params.set(key, value);
      }
    });
    if (page > 1) {
      params = params.set('page', String(page));
    }
    return this.http
      .get<Paginated<Listing> | Listing[]>(`${this.apiUrl}/listings/`, { params })
      .pipe(
        map((res) => ({
          items: isPaginated(res) ? res.results : Array.isArray(res) ? res : [],
          count: pageCount(res),
          hasNext: isPaginated(res) ? !!res.next : false,
          page,
        })),
      );
  }

  /** Filter values present in the published catalogue, with counts (for the store's filter panel). */
  facets(scope: { listing_type?: string; vendor?: string } = {}): Observable<StoreFacets> {
    let params = new HttpParams();
    Object.entries(scope).forEach(([key, value]) => {
      if (value) params = params.set(key, value);
    });
    return this.http.get<StoreFacets>(`${this.apiUrl}/listings/facets/`, { params });
  }

  listPaged(query: ListingQuery = {}, page = 1): Observable<ListingPage> {
    let params = new HttpParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value) {
        params = params.set(key, value);
      }
    });
    if (page > 1) {
      params = params.set('page', String(page));
    }
    return this.http
      .get<Paginated<Listing> | Listing[]>(`${this.apiUrl}/listings/`, { params })
      .pipe(
        map((res) => ({
          items: isPaginated(res) ? res.results : Array.isArray(res) ? res : [],
          count: pageCount(res),
          // Preview fixtures come back as a bare array with no `next`, which
          // correctly reads as a single complete page.
          hasNext: isPaginated(res) ? !!res.next : false,
          page,
        })),
      );
  }

  get(id: number | string): Observable<Listing> {
    return this.http.get<Listing>(`${this.apiUrl}/listings/${id}/`);
  }

  create(payload: Partial<Listing>): Observable<Listing> {
    return this.http.post<Listing>(`${this.apiUrl}/listings/`, payload);
  }

  update(id: number, payload: Partial<Listing>): Observable<Listing> {
    return this.http.patch<Listing>(`${this.apiUrl}/listings/${id}/`, payload);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/listings/${id}/`);
  }

  categories(): Observable<ListingCategory[]> {
    return this.http.get<Paginated<ListingCategory> | ListingCategory[]>(`${this.apiUrl}/listing-categories/`).pipe(unwrapPage<ListingCategory>());
  }

  licenseTypes(): Observable<LicenseType[]> {
    return this.http.get<Paginated<LicenseType> | LicenseType[]>(`${this.apiUrl}/license-types/`).pipe(unwrapPage<LicenseType>());
  }

  downloadUrl(listingId: number | string, format: string): string {
    return `${this.apiUrl}/listings/${listingId}/download/${format}/`;
  }

  /**
   * Fetches a purchased file as a blob rather than linking straight to
   * `downloadUrl`. The endpoint is IsAuthenticated and the JWT only gets
   * attached by MarketplaceAuthInterceptor on HttpClient calls — a plain
   * anchor href would arrive unauthenticated and 401.
   */
  download(listingId: number | string, format: string): Observable<Blob> {
    return this.http.get(this.downloadUrl(listingId, format), { responseType: 'blob' });
  }

  /**
   * "Get for free": records a $0 order for a free listing (they can't go
   * through Stripe checkout) and grants Content Vault ownership. Repeat
   * claims return the existing order with `already_owned: true`.
   */
  claim(listingId: number | string): Observable<{ order_id: number; order_number: string; already_owned: boolean }> {
    return this.http.post<{ order_id: number; order_number: string; already_owned: boolean }>(
      `${this.apiUrl}/listings/${listingId}/claim/`,
      {},
    );
  }

  /** One specific file of a listing (see `Listing.files`), as a blob — same auth reasoning as `download`. */
  downloadFile(listingId: number | string, fileId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/listings/${listingId}/files/${fileId}/download/`, {
      responseType: 'blob',
    });
  }
}
